import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import api from '../services/api';

const BookingContext = createContext(null);

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within BookingProvider');
  }
  return context;
};

export const BookingProvider = ({ children }) => {
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Bookings-list totals — populated from the paginated envelope so
  // BookingManagement can show a truncation banner if a real query ever
  // hits the hard ceiling.
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [bookingsTruncated, setBookingsTruncated] = useState(false);

  // Monotonic request id — every fetch increments this and only the
  // LATEST id is allowed to setState. Kills the "sometimes zero /
  // sometimes partial" race where a slow response overwrote a newer one.
  const fetchIdRef = useRef(0);
  const abortRef = useRef(null);

  // Fetch bookings from API. Always requests `limit=all&paginated=true` so
  // the admin bookings page never truncates (backed by the backend's
  // 20000-row hard ceiling, tracked via count_only in prod).
  const fetchBookings = useCallback(async (params = {}) => {
    // Abort any in-flight request — the new filter supersedes it.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const myId = ++fetchIdRef.current;

    setLoading(true);
    setError(null);
    try {
      const finalParams = { limit: 'all', paginated: 'true', ...params };
      const data = await api.getBookings(finalParams, { signal: ctrl.signal });
      // Late responses (an old request that finished after a newer one
      // already updated state) are dropped by comparing request ids.
      if (myId !== fetchIdRef.current) return [];
      // Support both new envelope and legacy array shape.
      const items = Array.isArray(data) ? data : (data?.items || []);
      const total = Array.isArray(data) ? items.length : (data?.total ?? items.length);
      const limitApplied = Array.isArray(data) ? items.length : (data?.limit ?? items.length);
      setBookings(items);
      setBookingsTotal(total);
      setBookingsTruncated(!Array.isArray(data) && items.length >= limitApplied && total > items.length);
      return items;
    } catch (err) {
      // Aborted requests are expected — don't surface them.
      if (err?.name === 'AbortError' || err?.code === 20 || String(err?.message || '').toLowerCase().includes('aborted')) {
        return [];
      }
      if (myId !== fetchIdRef.current) return [];
      setError(err.message);
      console.error('Error fetching bookings:', err);
      return [];
    } finally {
      if (myId === fetchIdRef.current) setLoading(false);
    }
  }, []);

  // Fetch blocked slots from API
  const fetchBlockedSlots = useCallback(async (date = '') => {
    try {
      const data = await api.getBlockedSlots(date);
      setBlockedSlots(data);
      return data;
    } catch (err) {
      console.error('Error fetching blocked slots:', err);
      return [];
    }
  }, []);

  // Add a new booking
  const addBooking = async (bookingData) => {
    try {
      const newBooking = await api.createBooking(bookingData);
      setBookings(prev => [...prev, newBooking]);
      return newBooking;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Update a booking
  const updateBooking = async (id, updates) => {
    try {
      const updatedBooking = await api.updateBooking(id, updates);
      setBookings(prev => prev.map(b => b.id === id ? updatedBooking : b));
      return updatedBooking;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Cancel a booking
  const cancelBooking = async (id, sendEmail = true, refundPercent = 0) => {
    try {
      const result = await api.cancelBooking(id, sendEmail, refundPercent);
      setBookings(prev => prev.map(b => 
        b.id === id ? { ...b, status: 'cancelled' } : b
      ));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Collect payment for a booking
  const collectPayment = async (id, paymentData) => {
    try {
      const updatedBooking = await api.collectPayment(id, paymentData);
      setBookings(prev => prev.map(b => b.id === id ? updatedBooking : b));
      return updatedBooking;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Delete a booking (actually cancels it)
  const deleteBooking = async (id) => {
    return cancelBooking(id);
  };

  // Add a blocked slot
  const addBlockedSlot = async (slotData) => {
    try {
      const newSlot = await api.createBlockedSlot(slotData);
      setBlockedSlots(prev => [...prev, newSlot]);
      return newSlot;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Remove a blocked slot
  const removeBlockedSlot = async (id) => {
    try {
      await api.deleteBlockedSlot(id);
      setBlockedSlots(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Get bookings by date (filters from current state or fetches)
  const getBookingsByDate = useCallback((date) => {
    return bookings.filter(b => b.date === date && b.status !== 'cancelled');
  }, [bookings]);

  // Get blocked slots by date
  const getBlockedSlotsByDate = useCallback((date) => {
    return blockedSlots.filter(s => s.date === date);
  }, [blockedSlots]);

  // Get slot availability for a date
  const getSlotAvailability = async (date) => {
    try {
      return await api.getSlotAvailability(date);
    } catch (err) {
      console.error('Error getting slot availability:', err);
      throw err;
    }
  };

  // Calculate price for a booking
  const calculatePrice = async (params) => {
    try {
      return await api.calculatePrice(params);
    } catch (err) {
      console.error('Error calculating price:', err);
      throw err;
    }
  };

  const value = {
    bookings,
    blockedSlots,
    loading,
    error,
    bookingsTotal,
    bookingsTruncated,
    fetchBookings,
    fetchBlockedSlots,
    addBooking,
    updateBooking,
    cancelBooking,
    deleteBooking,
    collectPayment,
    addBlockedSlot,
    removeBlockedSlot,
    getBookingsByDate,
    getBlockedSlotsByDate,
    getSlotAvailability,
    calculatePrice
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};
